/**
 * Paddle.js, loaded on demand, for the checkout overlay.
 *
 * REACT_APP_PADDLE_CLIENT_TOKEN is Paddle's client-side token (test_… for the
 * sandbox, live_… for production), safe to ship in client code. The token's
 * prefix picks the environment, so the web app and the server key cannot point
 * at different ones by accident.
 */

const PADDLE_JS_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";
const CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

export type PaddleCheckoutOutcome = "completed" | "closed";

let pendingCheckout: ((outcome: PaddleCheckoutOutcome) => void) | null = null;
let paddleReady: Promise<any> | null = null;

const settle = (outcome: PaddleCheckoutOutcome) => {
  const resolve = pendingCheckout;
  pendingCheckout = null;
  resolve?.(outcome);
};

const loadPaddle = (): Promise<any> => {
  if (paddleReady) {
    return paddleReady;
  }
  paddleReady = new Promise((resolve, reject) => {
    if (!CLIENT_TOKEN) {
      reject(new Error("REACT_APP_PADDLE_CLIENT_TOKEN is not set"));
      return;
    }
    const script = document.createElement("script");
    script.src = PADDLE_JS_URL;
    script.async = true;
    script.onload = () => {
      const Paddle = (window as any).Paddle;
      if (CLIENT_TOKEN.startsWith("test_")) {
        Paddle.Environment.set("sandbox");
      }
      Paddle.Initialize({
        token: CLIENT_TOKEN,
        eventCallback: (event: any) => {
          if (event?.name === "checkout.completed") {
            settle("completed");
          } else if (event?.name === "checkout.closed") {
            settle("closed");
          }
        }
      });
      resolve(Paddle);
    };
    script.onerror = () => reject(new Error("Could not load Paddle.js"));
    document.head.appendChild(script);
  });
  // A failed load must not stick: the next attempt retries it.
  paddleReady.catch(() => {
    paddleReady = null;
  });
  return paddleReady;
};

/** Fetches Paddle.js ahead of the click, while the user is still picking a plan. */
export const preloadPaddle = (): void => {
  loadPaddle().catch(() => undefined);
};

/**
 * Opens the overlay on a transaction the server created and resolves once the
 * buyer pays ("completed") or dismisses it ("closed").
 */
export const openPaddleCheckout = async (transactionId: string): Promise<PaddleCheckoutOutcome> => {
  const Paddle = await loadPaddle();
  settle("closed");
  return new Promise((resolve) => {
    pendingCheckout = resolve;
    // allowLogout: false hides "Not you? Change". A different email would move the
    // purchase to another Paddle customer than the one stored on the account.
    const theme = document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
    Paddle.Checkout.open({transactionId, settings: {displayMode: "overlay", allowLogout: false, theme}});
  });
};
